 'use client';

import Image from 'next/image';
import { Banner } from "@/components/shared/banner";
import { TableOfContents } from '@/features/marketing/components/blog/TableOfContents';
import { CustomButton } from "@/components/ui/custom-button";
import { Link } from "@/i18n/navigation";

export default function AccessibilityStatementPage() {

  const contentSections = [
    { id: 'statement', heading: 'Accessibility statement' },
    { id: 'feedback', heading: 'Feedback and contact information' },
    { id: 'enforcement', heading: 'Enforcement procedure' },
    { id: 'technical', heading: 'Technical information' },
    { id: 'improvements', heading: "What we're doing to improve accessibility" },
    { id: 'preparation', heading: 'Preparation of this statement' },
    { id: 'additional', heading: 'Additional information' },
  ];

  return (
    <main className="min-h-screen bg-white" aria-label="StudyStash Accessibility Statement">
      <Banner variant="with-svg" className="mb-10">
        <div className="text-white ml-8 lg:ml-16 xl:ml-24 max-w-[900px]">
          <p className="text-sm mb-4 font-bold text-white">
            Last Updated: 18th August 2025
          </p>
          <h1 className="text-4xl lg:text-5xl font-bold mb-2 leading-tight text-white whitespace-nowrap">Accessibility Statement</h1>
          <p className="text-lg lg:text-xl mb-4 leading-relaxed opacity-90 text-white">
            Our commitment to making StudyStash accessible for everyone
          </p>
        </div>
      </Banner>

      <div className="flex max-w-7xl mx-auto pb-8 sm:pb-16">
        {/* Sticky Sidebar */}
        <aside className="hidden lg:block w-96 shrink-0" role="complementary" aria-label="Table of contents">
          <div className="sticky top-0 pt-7 px-4 pb-16">
            <TableOfContents subject={{ contentSections }} />
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 lg:ml-8">
          <div className="max-w-4xl mx-auto lg:mx-0 px-3 sm:px-4 pt-2 sm:pt-4 lg:pt-8 pb-6 sm:pb-8 lg:px-8">

            <article className="prose prose-sm sm:prose-base lg:prose-lg max-w-none mt-6 sm:mt-8" role="article" aria-label="Accessibility statement content">
              <h2 id="statement" className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">
                Accessibility statement for StudyStash
              </h2>

              <p className="text-gray-700 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
                We're committed to ensuring StudyStash is accessible to everyone. Our platform is designed with accessibility in mind, following WCAG 2.2 AA standards to provide an inclusive learning experience for all users.
              </p>

              <Image 
                src="/collaborative-team.jpg" 
                alt="StudyStash Collaborative Team" 
                width={1200}
                height={675}
                className="w-full h-auto rounded-lg shadow-md"
              />
              
              <p className="text-gray-700 pt-4 mb-3 sm:mb-4 lg:mb-6 text-sm sm:text-base">
                This accessibility statement applies to the website <a href="https://studystash.com" className="text-blue-600 hover:text-blue-800 underline">StudyStash.com</a>.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                This website is run by StudyStash Inc. We want as many people as possible to be able to use this website. As far as possible, we aim that the website is:
              </p>

              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1">
                <li>technically accessible, in that it is possible for all users to access all information and functionality;</li>
                <li>equally usable, in that it is not prohibitively difficult or time consuming for users with disabilities to carry out normal tasks;</li>
                <li>capable of being adapted or configured by individual users to meet their specific needs and preferences;</li>
                <li>capable of interfacing with appropriate, widely available assistive technologies employed by users;</li>
              </ul>

              <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Our Accessibility Measures
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                At StudyStash we take the following measures to ensure accessibility of our products and services:
              </p>

              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1">
                <li>Include accessibility in our internal policies.</li>
                <li>Assign clear accessibility goals and responsibilities.</li>
                <li>Use formal accessibility quality assurance methods.</li>
              </ul>

              <section className="py-8 sm:py-16">
                <div className="max-w-full">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl lg:text-4xl font-bold mb-2 leading-tight text-gray-900">
                      Accessibility in Action
                    </h2>
                    
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                      Sometimes it's best to experience it for yourself - book a demo with our team today.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link href="/contact-us">
                        <CustomButton variant="outline" size="sm">
                          Contact us
                        </CustomButton>
                      </Link>
                      <Link href="/trust">
                        <CustomButton variant="outline" size="sm">
                          Trust Centre
                        </CustomButton>
                      </Link>
                    </div>
                  </div>

                  <Image
                    src="/student-teacher.jpg"
                    alt="Student and teacher working together on accessibility features"
                    width={1200}
                    height={675}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                </div>
              </section>

              <h4 id="feedback" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Feedback and contact information
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                If you need information on this website in a different format like accessible PDF, large print, easy read, audio recording or braille:
              </p>

              <ul className="list-disc pl-4 sm:pl-6 mb-4 sm:mb-6 text-gray-700 text-sm sm:text-base space-y-1">
                <li>Email <a href="mailto:support@studystash.com" className="text-blue-600 hover:text-blue-800 underline">support@studystash.com</a></li>
                <li>Visit your Customer Service Desk</li>
                <li><a href="https://studystash.com/contact-us" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">Contact us here</a></li>
              </ul>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We promise to get back to you in two working days.
              </p>

              <h4 id="enforcement" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Enforcement procedure
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                The Equality and Human Rights Commission (EHRC) is responsible for enforcing the Public Sector Bodies (Websites and Mobile Applications) (No. 2) Accessibility Regulations 2018 (the 'accessibility regulations'). If you're not happy with how we respond to your complaint, contact the Equality Advisory and Support Service (EASS).
              </p>

              <h4 id="technical" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Technical information about this website's accessibility
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                StudyStash Inc. is committed to making its website accessible, in accordance with the Public Sector Bodies (Websites and Mobile Applications) (No. 2) Accessibility Regulations 2018.
              </p>

              <h5 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mt-4 sm:mt-6 mb-3 sm:mb-4">
                Compliance status
              </h5>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
              The website has been tested against the <a href="https://www.w3.org/TR/WCAG22/" className="text-blue-600 hover:text-blue-800 underline">Web Content Accessibility Guidelines (WCAG)</a> 2.2 AA standard.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                StudyStash is fully compliant with the <a href="https://www.w3.org/TR/WCAG22/" className="text-blue-600 hover:text-blue-800 underline">Web Content Accessibility Guidelines (WCAG)</a> 2.2 AA standard.
              </p>
              
              <h4 id="improvements" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                What we're doing to improve accessibility
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                Our accessibility roadmap shows how and when we plan to improve accessibility on this website. You can access our <a href="https://trust.studystash.com/resources?s=q0qxzxiljdlk62y79osbh&name=accessibility-implementation-and-roadmap" className="text-blue-600 hover:text-blue-800 underline">accessibility implementation and roadmap</a> on our <a href="https://trust.studystash.com/" className="text-blue-600 hover:text-blue-800 underline">Trust Centre</a>.
              </p>

              <h4 id="preparation" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Preparation of this accessibility statement
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                This statement was prepared on Thursday 8th August, 2024 (08/08/2024). It was last reviewed on Monday 18th August, 2025 (18/08/2025).
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                This page has been designed in line with the <a href="https://www.gov.uk/government/publications/sample-accessibility-statement/sample-accessibility-statement-for-a-fictional-public-sector-website" className="text-blue-600 hover:text-blue-800 underline">GOV.UK sample accessibility statement</a> and follows the recommended formatting and structure.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                This website was last tested on 18/08/2025 against the WCAG 2.2 AA standard.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                The test was carried out by our internal technology team, led by our CTO, <Link href="/about/our-story" className="text-blue-600 hover:text-blue-800 underline">Jonathan Graham</Link>. The most viewed pages were tested using automated accessibility tools by our website team. In addition, a further audit of the website was conducted against the WCAG 2.2 AA standard.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                You can read the full accessibility test report in our <a href="https://trust.studystash.com/resources?s=3g6c0r7wbhyk59eap3jlkz&name=accessibility-conformance-report-(vpat-2-.-5-rev-508-)" className="text-blue-600 hover:text-blue-800 underline">Accessibility Conformance Report (VPAT)</a>.
              </p>

              <h4 id="additional" className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Additional information
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                We've also made the website text as simple as possible to understand.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                <a href="https://mcmw.abilitynet.org.uk/" className="text-blue-600 hover:text-blue-800 underline">
                  AbilityNet
                </a> has advice on making your device easier to use if you have a disability.
              </p>

              <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-16 sm:mt-20 mb-3 sm:mb-4">
                Download this accessibility statement
              </h4>

              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                You can download this accessibility statement in our <a href="https://trust.studystash.com/resources?s=zanc2bijx2x7sf8zv8g3yz&name=accessibility-statement" className="text-blue-600 hover:text-blue-800 underline">Trust Centre</a>.
              </p>
            </article>
          </div>
        </div>
      </div>
    </main>
  );
} 